import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makePalette } from 'material-color-tool';
import React, { useEffect, useState } from 'react';
import {
  Builder,
  BuilderProps,
  Config,
  ImmutableTree,
  Query,
  Utils
} from 'react-awesome-query-builder';
import { Filter } from '../Filter/filter-state';
import createAttributesViewModel from './attributes-view-model';
import loadConfig from './config';
import createQueryBuilderViewModel, { QueryBuilderViewModelType } from './query-builder-view-model';
import './styles.scss';
import useAttributeSearchParams from './useAttributesSearchParams';

const PREFIX = 'QueryBuilder';

const classes = {
  modal: `${PREFIX}-modal`,
  form: `${PREFIX}-form`,
  card: `${PREFIX}-card`,
  cardHeader: `${PREFIX}-cardHeader`,
  cardContent: `${PREFIX}-cardContent`,
  cardActions: `${PREFIX}-cardActions`,
  bgHover: `${PREFIX}-bgHover`,
  filterName: `${PREFIX}-filterName`,
  isPublic: `${PREFIX}-isPublic`,
  checkboxLabel: `${PREFIX}-checkboxLabel`,
  delete: `${PREFIX}-delete`
};

const StyledModal = styled(Modal)(({ theme }) => ({
  [`&.${classes.modal}`]: {
    padding: 10,
    paddingTop: 75
  },

  [`& .${classes.form}`]: {
    height: '100%',
    outline: 0
  },

  [`& .${classes.card}`]: {
    display: 'flex',
    flexFlow: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin',
    outline: 0
  },

  [`& .${classes.cardHeader}`]: {
    flex: '0 1 auto',
    marginTop: -6
  },

  [`& .${classes.cardContent}`]: {
    flex: '1 1 auto'
  },

  [`& .${classes.cardActions}`]: {
    flex: '0 1 40px',
    padding: 16
  },

  [`& .${classes.bgHover}`]: {
    '&:hover': {
      background: theme.palette.primary.light
    }
  },

  [`& .${classes.filterName}`]: {
    display: 'inline-block'
  },

  [`& .${classes.isPublic}`]: {
    float: 'right'
  },

  [`& .${classes.checkboxLabel}`]: {
    fontSize: '1.2rem',
    letterSpacing: '0.021rem',
    lineHeight: '16px',
    color: alpha(theme.palette.grey[900], 0.9)
  },

  [`& .${classes.delete}`]: {
    color: theme.palette.error.main
  }
}));

const { jsonLogicFormat, checkTree, loadFromJsonLogic, isValidTree } = Utils;

interface QueryBuilderProps {
  filter?: Filter;
  elementId?: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: any) => Promise<boolean>;
  onDelete?: (filterToDelete: Filter) => void;
}

const QueryBuilder = ({
  filter,
  onSubmit,
  isOpen,
  onClose,
  onDelete,
  elementId = ''
}: QueryBuilderProps) => {
  const theme = useTheme();
  const shadePalette = makePalette(theme.palette.secondary.main);
  const checkboxColor = shadePalette[7];

  const viewModel: QueryBuilderViewModelType = createQueryBuilderViewModel(elementId);

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [filterName, setFilterName] = useState<string>('');
  const [isPublic, setIsPublic] = useState(false);
  const [config, setConfig] = useState<Config | undefined>();
  const [tree, setTree] = useState<ImmutableTree | undefined>();

  const emptyTree = { and: [{ in: ['', { var: 'name' }] }] };
  const addSettingsToConfig = (configuration: Config) => {
    return {
      ...configuration,
      settings: { ...configuration.settings, showErrorMessage }
    };
  };

  const getTree = (jsonLogic: Object) => {
    if (!config) return undefined;
    const configuration = addSettingsToConfig(config);

    return checkTree(loadFromJsonLogic(jsonLogic, configuration), configuration);
  };

  const clearTree = () => {
    setFilterName('');
    setTree(getTree(emptyTree));
    setIsPublic(false);
    setShowErrorMessage(false);
  };

  const handleCancel = () => {
    setTree(getTree(filter?.rule || emptyTree));
    setFilterName(filter?.name || '');
    setIsPublic(filter?.isPublic || false);
    setShowErrorMessage(false);
    onClose();
  };

  const handleDelete = (filterToDelete: Filter) => (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (onDelete) {
      onDelete(filterToDelete);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    const submitSuccess = await onSubmit({
      _id: data.get('_id'),
      name: data.get('filterName'),
      rule: data.get('rule')?.toString(),
      isPublic: data.get('isPublic') || false
    });

    if (submitSuccess) {
      clearTree();
      onClose();
    }
  };

  const attributesViewModel = createAttributesViewModel();
  const attributes = useAttributeSearchParams();

  useEffect(() => {
    if (!isOpen || !config) return;

    const initTree = () => {
      if (filter) {
        setTree(getTree(filter?.rule || emptyTree));
        setFilterName(filter?.name || '');
        setIsPublic(filter?.isPublic || false);
      } else {
        clearTree();
      }
    };

    initTree();
  }, [filter, isOpen, config === undefined]);

  useEffect(() => {
    if (!isOpen) return;
    const initConfig = async () => {
      const attributesData = await attributesViewModel.getAttributes(attributes || []);

      if (!attributesData.error) {
        setConfig(loadConfig(viewModel.getOptions, attributesData.data));
      }
    };

    initConfig();
  }, [attributes?.toString(), isOpen]);

  const renderBuilder = (props: BuilderProps) => (
    <div className="query-builder-container">
      <div className="query-builder">
        <Builder {...props} />
      </div>
    </div>
  );

  const onChange = (immutableTree: ImmutableTree) => {
    setShowErrorMessage(true);
    setTree(immutableTree);
  };

  const getJsonLogic = (immutableTree: ImmutableTree) => {
    if (!config) return '';
    const { logic } = jsonLogicFormat(immutableTree, addSettingsToConfig(config));
    if (!logic) return '';
    return JSON.stringify(logic, undefined, 2);
  };

  const isSaveDisabled = () => {
    if (filterName === '' || !tree || !getJsonLogic(tree)) return true;
    return !isValidTree(tree);
  };

  const handlePublicClick = () => {
    setIsPublic(!isPublic);
  };

  return !isOpen || !config || !tree ? null : (
    <StyledModal
      data-testid="query-builder-modal"
      aria-labelledby="query-builder"
      aria-describedby="query-builder"
      className={classes.modal}
      open={isOpen}
      onClose={handleCancel}
    >
      <form
        autoComplete="off"
        noValidate
        className={classes.form}
        onSubmit={handleSubmit}
        data-testid="query-builder-form"
      >
        <Card className={`${classes.card}`} data-testid="query-builder-main">
          <CardHeader
            title="Add filter"
            className={classes.cardHeader}
            titleTypographyProps={{ align: 'left', variant: 'h5' }}
          />
          <CardContent className={classes.cardContent}>
            <Grid container>
              <Grid item xs={12}>
                <Typography variant="h6" color="primary" className={classes.filterName}>
                  Filter name
                </Typography>
                <FormControlLabel
                  classes={{ root: classes.isPublic, label: classes.checkboxLabel }}
                  control={
                    <Checkbox
                      checked={isPublic}
                      onChange={handlePublicClick}
                      id="isPublic"
                      color="default"
                      name="isPublic"
                      value={isPublic}
                      style={{ color: checkboxColor }}
                    />
                  }
                  label="Public"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <input
                  type="hidden"
                  id="_id"
                  name="_id"
                  value={filter?._id || ''}
                  data-testid="id-input"
                />
                <TextField
                  inputProps={{ 'data-testid': 'name-input', className: classes.bgHover }}
                  variant="outlined"
                  margin="normal"
                  required
                  fullWidth
                  id="filterName"
                  name="filterName"
                  placeholder=""
                  value={filterName || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFilterName(event.target.value);
                  }}
                />
                <input type="hidden" id="rule" name="rule" value={getJsonLogic(tree)} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" color="primary">
                  Show elements where
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Query
                  {...addSettingsToConfig(config)}
                  value={tree}
                  onChange={onChange}
                  renderBuilder={renderBuilder}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions className={classes.cardActions}>
            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid item xs={12}>
                <Divider orientation="horizontal" />
              </Grid>
              {onDelete && filter ? (
                <Grid item>
                  <Button
                    type="button"
                    onClick={handleDelete(filter)}
                    className={classes.delete}
                    data-testid="query-builder-delete-button"
                  >
                    Delete
                  </Button>
                </Grid>
              ) : null}
              <Grid item>
                <Button
                  type="button"
                  onClick={handleCancel}
                  data-testid="query-builder-cancel-button"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <Button
                  color="primary"
                  type="submit"
                  variant={isSaveDisabled() ? 'contained' : 'text'}
                  data-testid="query-builder-save-button"
                  disabled={isSaveDisabled()}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </CardActions>
        </Card>
      </form>
    </StyledModal>
  );
};

export default QueryBuilder;
